import React, { useState, useEffect, useCallback } from 'react';
import BackOfficeLayout from '../../components/backoffice/BackOfficeLayout';
import { useAuth } from '../../context/AuthContext';
import { useEnvironment } from '../../context/EnvironmentContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { FileCheck, Shield, AlertTriangle, Users, Ban, RefreshCw } from 'lucide-react';
import { toast } from '../../lib/safeToast';

export default function BackOfficeAnalyticsCompliance() {
  const { api } = useAuth();
  const { isLive } = useEnvironment();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/backoffice/analytics/compliance');
      setData(res.data?.data || null);
    } catch { toast.error('Failed to load compliance data'); } finally { setLoading(false); }
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData, isLive]);

  const cards = data ? [
    { label: 'Total Users', value: data.total_users, icon: Users, color: 'text-slate-700', bg: 'bg-slate-50' },
    { label: 'KYC Approved', value: data.kyc_approved, icon: FileCheck, color: 'text-green-700', bg: 'bg-green-50' },
    { label: 'KYC Pending', value: data.kyc_pending, icon: AlertTriangle, color: 'text-amber-700', bg: 'bg-amber-50' },
    { label: '2FA Enabled', value: `${data.two_fa_enabled} (${data.two_fa_pct}%)`, icon: Shield, color: 'text-blue-700', bg: 'bg-blue-50' },
    { label: 'Suspended', value: data.suspended, icon: Ban, color: 'text-red-700', bg: 'bg-red-50' },
  ] : [];

  return (
    <BackOfficeLayout>
      <div className="space-y-8">
        <div className="flex items-center justify-between pb-4">
          <div>
            <h1 className="text-2xl font-bold text-[#073B2E] flex items-center gap-2"><FileCheck className="w-7 h-7 text-[#0B6445]" />Compliance Summary</h1>
            <p className="text-[#0E1B16]/70 mt-2">Security and compliance metrics overview.</p>
          </div>
          <Button variant="outline" size="sm" onClick={fetchData} className="border-[#1C472B] text-[#1C472B] hover:bg-[#E6F7F3] hover:text-[#073B2E]"><RefreshCw className="w-4 h-4 mr-1" />Refresh</Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><div className="spinner w-8 h-8" /></div>
        ) : !data ? (
          <Card className="border-[#DCE4E0] shadow-sm"><CardContent className="pt-6"><p className="text-center py-12 text-[#0E1B16]/60">No data available.</p></CardContent></Card>
        ) : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-5">
              {cards.map((c) => (
                <Card key={c.label} className="border-[#DCE4E0] shadow-sm">
                  <CardContent className="p-5">
                    <div className={`inline-flex items-center justify-center w-10 h-10 rounded-lg ${c.bg} mb-3`}>
                      <c.icon className={`w-5 h-5 ${c.color}`} />
                    </div>
                    <p className="text-2xl font-bold text-[#073B2E]">{c.value}</p>
                    <p className="text-xs text-slate-500 mt-1">{c.label}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            <Card className="border-[#DCE4E0] shadow-sm mt-10">
              <CardHeader className="pb-3 pt-6"><CardTitle className="text-base text-[#073B2E]">Compliance Checklist</CardTitle></CardHeader>
              <CardContent className="pb-6">
                <div className="space-y-4">
                  {[
                    { check: 'KYC completion rate', value: data.total_users ? `${Math.round(data.kyc_approved / data.total_users * 100)}%` : '0%', ok: data.kyc_approved > 0 },
                    { check: '2FA adoption rate', value: `${data.two_fa_pct}%`, ok: data.two_fa_pct >= 50 },
                    { check: 'Suspended accounts monitored', value: `${data.suspended} account(s)`, ok: true },
                    { check: 'No unreviewed KYC backlog', value: data.kyc_pending === 0 ? 'Clear' : `${data.kyc_pending} pending`, ok: data.kyc_pending === 0 },
                  ].map((item) => (
                    <div key={item.check} className="flex items-center justify-between py-3 border-b border-[#DCE4E0]/60 last:border-0">
                      <span className="text-sm text-[#0F172A]">{item.check}</span>
                      <span className={`text-sm font-medium ${item.ok ? 'text-green-600' : 'text-amber-600'}`}>{item.value}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </BackOfficeLayout>
  );
}
